<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  context: { type: Object, required: true },
});

const copied = ref('');
const copyValue = async (key, value) => {
  if (!navigator?.clipboard?.writeText || !value) return;
  await navigator.clipboard.writeText(value);
  copied.value = key;
  window.setTimeout(() => {
    if (copied.value === key) copied.value = '';
  }, 1200);
};
</script>

<template>
  <div class="flex flex-col gap-3 p-3">
    <div class="flex items-center gap-2 flex-wrap">
      <span class="px-2 py-1 text-xs rounded-md bg-n-blue-3 text-n-blue-11">
        {{ context.channel }}
      </span>
      <span class="px-2 py-1 text-xs rounded-md bg-n-slate-3 text-n-slate-11">
        {{ context.attributionQuality }}
      </span>
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-xs text-n-slate-9">{{
        $t('CONVERSATION.META_AD_CONTEXT.CURRENT_TOUCH')
      }}</span>
      <strong class="text-sm text-n-slate-12">{{
        context.adName || context.title || context.adId
      }}</strong>
      <span v-if="context.campaignName" class="text-xs text-n-slate-11">{{
        context.campaignName
      }}</span>
      <span v-if="context.adsetName" class="text-xs text-n-slate-11">{{
        context.adsetName
      }}</span>
    </div>

    <div class="grid gap-2">
      <div
        v-for="item in [
          ['ad', $t('CONVERSATION.META_AD_CONTEXT.FIELDS.AD_ID'), context.adId],
          [
            'adset',
            $t('CONVERSATION.META_AD_CONTEXT.FIELDS.ADSET_ID'),
            context.adsetId,
          ],
          [
            'campaign',
            $t('CONVERSATION.META_AD_CONTEXT.FIELDS.CAMPAIGN_ID'),
            context.campaignId,
          ],
          [
            'creative',
            $t('CONVERSATION.META_AD_CONTEXT.FIELDS.CREATIVE_ID'),
            context.creativeId,
          ],
          [
            'ctwa',
            $t('CONVERSATION.META_AD_CONTEXT.FIELDS.CTWA_CLID'),
            context.ctwaClid,
          ],
        ].filter(([, , value]) => value)"
        :key="item[0]"
        class="flex items-center gap-2"
      >
        <div class="min-w-0 flex-1">
          <span class="block text-xs text-n-slate-9">{{ item[1] }}</span>
          <code class="text-xs break-all text-n-slate-12">{{ item[2] }}</code>
        </div>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          :icon="copied === item[0] ? 'i-lucide-check' : 'i-lucide-copy'"
          @click="copyValue(item[0], item[2])"
        />
      </div>
    </div>

    <div v-if="context.firstTouchAdId" class="pt-2 border-t border-n-weak">
      <span class="block text-xs text-n-slate-9">{{
        $t('CONVERSATION.META_AD_CONTEXT.FIRST_TOUCH')
      }}</span>
      <code class="text-xs break-all text-n-slate-12">{{
        context.firstTouchAdId
      }}</code>
      <code
        v-if="context.firstTouchCampaignId"
        class="block mt-1 text-xs break-all text-n-slate-10"
      >
        {{ context.firstTouchCampaignId }}
      </code>
    </div>

    <div class="flex flex-wrap gap-2">
      <a
        v-if="context.linkUrl"
        :href="context.linkUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs font-medium text-n-blue-11 hover:underline"
      >
        {{ $t('CONVERSATION.META_AD_CONTEXT.OPEN_AD') }}
      </a>
      <a
        v-if="context.adsManagerUrl"
        :href="context.adsManagerUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs font-medium text-n-blue-11 hover:underline"
      >
        {{ $t('CONVERSATION.META_AD_CONTEXT.OPEN_ADS_MANAGER') }}
      </a>
    </div>
  </div>
</template>
